import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import {
  CardCvcElement as CardCvcJs,
  CardExpiryElement as CardExpiryJs,
  CardNumberElement as CardNumberJs,
} from '@stripe/react-stripe-js';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useState } from 'react';
import { CardCvcElement, CardExpiryElement, CardNumberElement } from 'react-stripe-elements';

import inputStyles from '../Input/styles';
import Caption from '../Typography/Caption';
import { LIBRARY_TYPES } from './constants';
import stripeElementStyle from './styles';

export function StripeInput({ formIsSubmitted, name, cardError, setCardElementError, label, libraryType }) {
  const createErrorMessage = message => {
    let fieldName;

    switch (name) {
      case 'cardExpiry':
        fieldName = 'expiration';
        break;
      case 'cardNumber':
        fieldName = 'card number';
        break;
      case 'cardCvc':
        fieldName = 'security code';

      // no default
    }

    const reason = message.indexOf('incomplete') > -1 ? 'Incomplete' : 'Invalid';

    return `${reason} ${fieldName}`;
  };

  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(createErrorMessage('incomplete'));
  const CardNumber = libraryType === LIBRARY_TYPES.stripeElement ? CardNumberElement : CardNumberJs;
  const CardExpiry = libraryType === LIBRARY_TYPES.stripeElement ? CardExpiryElement : CardExpiryJs;
  const CardCvc = libraryType === LIBRARY_TYPES.stripeElement ? CardCvcElement : CardCvcJs;

  useEffect(() => {
    setTouched(!!formIsSubmitted);
  }, [formIsSubmitted]);

  useEffect(() => {
    if (setCardElementError) {
      setCardElementError(name, error);
    }
  }, [error, name, setCardElementError]);

  const handleBlur = () => {
    setTouched(true);
  };

  const handleChange = event => {
    if (event.error) {
      const errorMessage = createErrorMessage(event.error.message);
      setError(errorMessage);
    } else if (event.complete) {
      setError(null);
    }
  };

  const classes = inputStyles({ size: 'large' });
  const elementProps = {
    onBlur: handleBlur,
    onChange: handleChange,
  };

  const elementOptions = options => {
    if (libraryType === LIBRARY_TYPES.stripeElement) {
      return options;
    }
    return { options };
  };

  let cardNode = null;
  switch (name) {
    case 'cardNumber':
      cardNode = (
        <CardNumber
          {...elementOptions({
            classes: {
              base: classNames(classes.root, classes.stripeInput, {
                error: touched && (error || cardError),
              }),
              focus: classes.focused,
            },
            style: stripeElementStyle,
          })}
          {...elementProps}
        />
      );
      break;
    case 'cardExpiry':
      cardNode = (
        <CardExpiry
          {...elementOptions({
            classes: {
              base: classNames(classes.root, classes.stripeInput, {
                error: touched && (error || cardError),
              }),
              focus: classes.focused,
            },
            style: stripeElementStyle,
          })}
          {...elementProps}
        />
      );
      break;
    case 'cardCvc':
      cardNode = (
        <CardCvc
          {...elementOptions({
            classes: {
              base: classNames(classes.root, classes.stripeInput, {
                error: touched && (error || cardError),
              }),
              focus: classes.focused,
            },
            placeholder: '123',
            style: stripeElementStyle,
          })}
          {...elementProps}
        />
      );
      break;
    default:
      cardNode = null;
  }

  return (
    <FormControl
      classes={{
        root: classes.formControl,
      }}
    >
      {label && (
        <Typography classes={{ root: classes.label }} variant="body2">
          {label}
        </Typography>
      )}
      {cardNode}
      {touched && (error || cardError) && (
        <Caption align="right" classes={{ root: classes.errorMessage }} color="error">
          {error || cardError}
        </Caption>
      )}
    </FormControl>
  );
}

StripeInput.propTypes = {
  cardError: PropTypes.string,
  formIsSubmitted: PropTypes.bool,
  label: PropTypes.string,
  libraryType: PropTypes.string,
  name: PropTypes.string.isRequired,
  setCardElementError: PropTypes.func,
};

StripeInput.defaultProps = {
  libraryType: LIBRARY_TYPES.stripeElement,
};

export default memo(StripeInput);
